import { api } from './client'

export type HoldingBucket = 'core' | 'event'

export type Holding = {
  id: string
  symbol: string
  qty: number
  cost_basis?: number | null
  bucket: HoldingBucket
  event_id?: string | null
  thesis?: string | null
  source: 'manual' | 'assignment'
  position_id?: string | null
  opened_at: string
  closed_at?: string | null
}

export type HoldingInput = {
  symbol: string
  qty: number
  cost_basis?: number
  bucket: HoldingBucket
  event_id?: string
  thesis?: string
  source?: 'manual' | 'assignment'
  position_id?: string
}

export type AccountSnapshot = {
  currency: string
  net_liq?: number | null
  margin_debt?: number | null
  reserved_cash?: number | null
  maintenance_pct?: number | null
  updated_at?: string
}

export type AssignmentSuggestion = {
  symbol: string
  qty: number
  cost_basis_suggested: number
  premiums_total: number
  strike: number
}

export type CCCandidate = {
  strike: number
  expiry: string
  dte: number
  bid: number
  ask: number
  mid: number
  delta: number
  open_interest: number
  volume: number
  iv: number
  premium_per_contract: number
  apy: number
}

export type CCOpenCall = {
  position_id: string
  strike: number
  expiry: string
  dte: number
}

export type CCCandidates = {
  holding: { id: string; symbol: string; qty: number; cost_basis: number | null }
  as_of: string
  spot: number
  spot_source: 'live' | 'close'
  anchor: { kind: 'assigned_strike' | 'cost_basis' | 'spot'; value: number }
  depth: 'shallow' | 'deep' | 'unknown'
  depth_pct: number | null
  rule: { dte_min: number; dte_max: number; strike_floor: number; oi_min: number }
  chain_available: boolean
  open_calls: CCOpenCall[]
  candidates: CCCandidate[]
}

export function listHoldings(): Promise<{ items: Holding[] }> {
  return api<{ items: Holding[] }>('/v1/holdings')
}

export function createHolding(body: HoldingInput): Promise<Holding> {
  return api<Holding>('/v1/holdings', { method: 'POST', body: JSON.stringify(body) })
}

export function updateHolding(id: string, body: Partial<HoldingInput> & { closed_at?: string }): Promise<Holding> {
  return api<Holding>(`/v1/holdings/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}

export function deleteHolding(id: string): Promise<unknown> {
  return api(`/v1/holdings/${id}`, { method: 'DELETE' })
}

export function fetchAssignmentSuggestion(positionId: string): Promise<AssignmentSuggestion> {
  return api<AssignmentSuggestion>(`/v1/holdings/assignment_suggestion?position_id=${encodeURIComponent(positionId)}`)
}

export function fetchCCCandidates(holdingId: string): Promise<CCCandidates> {
  return api<CCCandidates>(`/v1/holdings/${holdingId}/cc_candidates`)
}

export function fetchAccount(): Promise<AccountSnapshot> {
  return api<AccountSnapshot>('/v1/account')
}

export function saveAccount(body: Partial<AccountSnapshot>): Promise<AccountSnapshot> {
  return api<AccountSnapshot>('/v1/account', { method: 'PUT', body: JSON.stringify(body) })
}
